import { create } from 'zustand';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { User, UserRole } from '../types';

const USER_ROLES: UserRole[] = [
  'admin',
  'project_manager',
  'site_manager',
  'finance',
  'manager',
  'sales',
  'organizer',
  'site_engineer',
  'customer',
  'vendor',
  'custom_staff',
];

function roleFromUnknown(raw: unknown): UserRole | null {
  if (typeof raw !== 'string') return null;
  return USER_ROLES.includes(raw as UserRole) ? (raw as UserRole) : null;
}

/** Last known role when `public.users` is unreachable (offline). Keyed per auth user id. */
function cachedRoleStorageKey(userId: string): string {
  return `pm-auth-role:${userId}`;
}

function readCachedRole(userId: string): UserRole | null {
  try {
    return roleFromUnknown(sessionStorage.getItem(cachedRoleStorageKey(userId)));
  } catch {
    return null;
  }
}

function writeCachedRole(userId: string, role: UserRole) {
  try {
    sessionStorage.setItem(cachedRoleStorageKey(userId), role);
  } catch {
    /* private mode / quota */
  }
}

function clearCachedRole(userId: string) {
  try {
    sessionStorage.removeItem(cachedRoleStorageKey(userId));
  } catch {
    /* noop */
  }
}

/** Max wait for Supabase INITIAL_SESSION on cold start (slow/blocked networks). */
const AUTH_BOOTSTRAP_TIMEOUT_MS = 8000;

interface AuthState {
  user: User | null;
  loading: boolean;
  /** True until the first auth session check finishes or times out. */
  sessionPending: boolean;
  /**
   * From `session.user.user_metadata.portal_password_set` at last successful session apply.
   * `null` when signed out. Used only for customer portal routes (avoid flaky re-fetch offline).
   */
  portalPasswordSet: boolean | null;
  /** From public.users.customer_id - used for portal onboarding gate. */
  portalOnboardingComplete: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-read JWT session + public.users profile (e.g. after updateUser metadata or password). */
  refreshSession: () => Promise<void>;
  updateProfile: (data: { name?: string; phone?: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

function isPortalAuthUser(session: Session): boolean {
  const meta = session.user.user_metadata ?? {};
  return (
    meta.customer_portal === true ||
    meta.portal_signup === true ||
    meta.vendor_portal === true
  );
}

function isEmailConfirmed(session: Session): boolean {
  const u = session.user;
  return Boolean(u.email_confirmed_at ?? (u as { confirmed_at?: string | null }).confirmed_at);
}

export const useAuthStore = create<AuthState>((set, get) => {
  let bootstrapTimer: ReturnType<typeof setTimeout> | null = null;

  const finishBootstrap = () => {
    if (bootstrapTimer) {
      clearTimeout(bootstrapTimer);
      bootstrapTimer = null;
    }
    if (get().sessionPending) {
      set({ sessionPending: false });
    }
  };

  bootstrapTimer = setTimeout(() => {
    bootstrapTimer = null;
    if (!get().sessionPending) return;
    console.warn('[authStore] auth bootstrap timed out - continuing without session');
    set({ sessionPending: false, loading: false });
  }, AUTH_BOOTSTRAP_TIMEOUT_MS);

  const applySession = async (session: Session | null, opts?: { silent?: boolean }) => {
    if (!session?.user) {
      finishBootstrap();
      set({ user: null, loading: false, portalPasswordSet: null, portalOnboardingComplete: false });
      return;
    }

    if (isPortalAuthUser(session) && !isEmailConfirmed(session)) {
      if (opts?.silent) return;
      await supabase.auth.signOut();
      finishBootstrap();
      set({ user: null, loading: false, portalPasswordSet: null, portalOnboardingComplete: false });
      return;
    }

    const portalPasswordSet = session.user.user_metadata?.portal_password_set === true;
    const portalOnboardingComplete =
      session.user.user_metadata?.portal_onboarding_complete === true;
    const prev = get().user;

    // Use maybeSingle + explicit column list to avoid select(*) pulling unexpected
    // columns and to avoid the 406-vs-5xx ambiguity with .single().
    // role_id/roles(...) is a nested embed resolving the user's configurable
    // role name + granted permission keys (public.roles/role_permissions/permissions).
    const { data: profile, error } = await supabase
      .from('users')
      .select(
        'id,name,email,role,phone,is_active,customer_id,role_id,roles(name,is_system,role_permissions(permissions(key)))'
      )
      .eq('id', session.user.id)
      .maybeSingle();

    if (!error && profile) {
      const r = profile.role as UserRole;
      writeCachedRole(profile.id, r);
      const roleRow = profile.roles as unknown as
        | { name: string; is_system: boolean; role_permissions: { permissions: { key: string } }[] }
        | null;
      const permissionKeys =
        roleRow?.role_permissions?.map((rp) => rp.permissions.key) ?? [];
      const nextUser: User = {
        id: profile.id,
        name: profile.name,
        email: profile.email,
        role: r,
        phone: profile.phone || undefined,
        isActive: profile.is_active,
        customerId: profile.customer_id ?? null,
        roleId: profile.role_id ?? undefined,
        roleName: roleRow?.name,
        permissionKeys,
        isCustomRole: roleRow ? !roleRow.is_system : false,
      };
      const onboardingComplete =
        portalOnboardingComplete || profile.customer_id != null;
      // Silent refresh: skip store update when profile unchanged (avoids remounting routes).
      if (
        opts?.silent &&
        prev &&
        prev.id === nextUser.id &&
        prev.name === nextUser.name &&
        prev.email === nextUser.email &&
        prev.role === nextUser.role &&
        prev.phone === nextUser.phone &&
        prev.isActive === nextUser.isActive &&
        prev.customerId === nextUser.customerId &&
        prev.roleId === nextUser.roleId &&
        (prev.permissionKeys ?? []).slice().sort().join(',') ===
          permissionKeys.slice().sort().join(',') &&
        get().portalPasswordSet === portalPasswordSet &&
        get().portalOnboardingComplete === onboardingComplete
      ) {
        return;
      }
      finishBootstrap();
      set({
        user: nextUser,
        loading: false,
        portalPasswordSet,
        portalOnboardingComplete: onboardingComplete,
      });
      return;
    }

    // Fallback: `public.users` unavailable (offline, RLS, 5xx). Never default staff to
    // `customer` - that sent internal users to the portal + set-password flow.
    const meta = session.user.user_metadata ?? {};
    const appMeta = (session.user as { app_metadata?: Record<string, unknown> }).app_metadata ?? {};
    const jwtRole = roleFromUnknown(appMeta.role) ?? roleFromUnknown(meta.role);
    const sameId = prev && prev.id === session.user.id;
    const cached = readCachedRole(session.user.id);
    const portalFlag =
      meta.customer_portal === true ||
      meta.customer_portal === 'true' ||
      meta.customer_portal === 1;
    const vendorFlag =
      meta.vendor_portal === true ||
      meta.vendor_portal === 'true' ||
      meta.vendor_portal === 1;

    let fallbackRole: UserRole;
    if (jwtRole) {
      fallbackRole = jwtRole;
    } else if (sameId) {
      fallbackRole = prev.role;
    } else if (cached) {
      fallbackRole = cached;
    } else if (vendorFlag) {
      fallbackRole = 'vendor';
    } else if (portalFlag) {
      fallbackRole = 'customer';
    } else {
      // Cold start offline with no cache - cannot know role; stay out of customer portal.
      fallbackRole = 'sales';
    }

    const fallbackName =
      (typeof meta.name === 'string' && meta.name ? meta.name : null) ??
      (sameId ? prev.name : null) ??
      session.user.email ??
      'User';

    console.warn('[authStore] users table query failed - using JWT fallback:', error?.message);

    const fallbackUser: User = {
      id: session.user.id,
      name: fallbackName,
      email: session.user.email ?? '',
      role: fallbackRole,
      phone: sameId ? prev.phone : undefined,
      isActive: sameId ? prev.isActive : true,
      customerId: sameId ? prev.customerId : null,
      // `public.users` is unreachable in this path - carry over the last known
      // role/permissions rather than guessing; empty means "no permissions"
      // (fails safe) until the next successful session apply.
      roleId: sameId ? prev.roleId : undefined,
      roleName: sameId ? prev.roleName : undefined,
      permissionKeys: sameId ? prev.permissionKeys : [],
      isCustomRole: sameId ? prev.isCustomRole : false,
    };
    const fallbackOnboarding =
      portalOnboardingComplete || (sameId && prev.customerId != null);
    if (
      opts?.silent &&
      prev &&
      prev.id === fallbackUser.id &&
      prev.name === fallbackUser.name &&
      prev.email === fallbackUser.email &&
      prev.role === fallbackUser.role &&
      prev.phone === fallbackUser.phone &&
      prev.isActive === fallbackUser.isActive &&
      prev.customerId === fallbackUser.customerId &&
      get().portalPasswordSet === portalPasswordSet &&
      get().portalOnboardingComplete === fallbackOnboarding
    ) {
      return;
    }

    finishBootstrap();
    set({
      user: fallbackUser,
      loading: false,
      portalPasswordSet,
      portalOnboardingComplete: fallbackOnboarding,
    });
  };

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      const id = get().user?.id ?? session?.user?.id;
      if (id) clearCachedRole(id);
      finishBootstrap();
      set({ user: null, loading: false, portalPasswordSet: null, portalOnboardingComplete: false });
      return;
    }

    const hasUser = !!get().user;
    // Tab focus / cross-tab sync can emit TOKEN_REFRESHED or SIGNED_IN while already signed in.
    const isBackgroundSync =
      hasUser &&
      (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED' || event === 'SIGNED_IN');

    if (isBackgroundSync) {
      void applySession(session, { silent: true });
      return;
    }

    const needsBlockingLoad = event === 'SIGNED_IN' || event === 'INITIAL_SESSION';
    if (needsBlockingLoad && !hasUser) {
      set({ loading: true });
    }

    void applySession(session);
  });

  // Proactive session read - covers cases where INITIAL_SESSION is delayed on slow networks.
  void supabase.auth.getSession().then(({ data: { session } }) => {
    if (!get().sessionPending) return;
    void applySession(session);
  }).catch(() => {
    finishBootstrap();
    set({ loading: false });
  });

  return {
    user: null,
    loading: false,
    sessionPending: true,
    portalPasswordSet: null,
    portalOnboardingComplete: false,
    refreshSession: async () => {
      const hadUser = !!get().user;
      if (!hadUser) set({ loading: true });
      const {
        data: { session },
      } = await supabase.auth.getSession();
      await applySession(session, { silent: hadUser });
    },
    login: async (email: string, password: string) => {
      set({ loading: true });
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        set({ loading: false });
        throw error;
      }
    },
    logout: async () => {
      const id = get().user?.id;
      set({ loading: true });
      await supabase.auth.signOut();
      if (id) clearCachedRole(id);
      set({ user: null, loading: false, portalPasswordSet: null, portalOnboardingComplete: false });
    },
    updateProfile: async (data: { name?: string; phone?: string }) => {
      const state = get();
      if (!state.user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('users')
        .update({
          name: data.name ?? state.user.name,
          phone: data.phone ?? state.user.phone ?? null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', state.user.id);

      if (error) throw error;

      set({
        user: {
          ...state.user,
          name: data.name ?? state.user.name,
          phone: data.phone ?? state.user.phone,
        },
      });
    },
    changePassword: async (currentPassword: string, newPassword: string) => {
      const state = get();
      if (!state.user) throw new Error('Not authenticated');

      const { data: sessionData } = await supabase.auth.getSession();
      const authEmail = sessionData.session?.user.email;
      if (!authEmail) throw new Error('Not authenticated');

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: authEmail,
        password: currentPassword,
      });
      if (signInError) throw new Error('Current password is incorrect');

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
    },
  };
});
